/**
 * Unified shift status type system
 * Single source of truth for all shift status types, enums, and metadata
 */

import { Database } from '@/integrations/supabase/types';
import {
  Clock,
  PlayCircle,
  Play,
  CheckCircle,
  AlertCircle,
  XCircle,
  UserX,
  ArrowRightFromLine,
  Calendar,
  LucideIcon
} from 'lucide-react';

// Core database types
export type Shift = Database['public']['Tables']['shifts']['Row'];
export type Employee = Database['public']['Tables']['employees']['Row'];
export type TimeLog = Database['public']['Tables']['time_logs']['Row'];

// Unified shift status enum
export enum ShiftStatus {
  PLANNING = 'planning',
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  COMPLETED_LATE = 'completed_late',
  MISSED = 'missed',
  UNASSIGNED = 'unassigned',
  ON_TIME = 'on_time',
  LATE = 'late',
  TOO_EARLY = 'too_early',
  LEFT_EARLY = 'left_early',
  OVERTIME = 'overtime',
  NO_DEPARTURE_SCAN = 'no_departure_scan',
  // Composite statuses (arrival + departure combinations)
  TOO_EARLY_OVERTIME = 'too_early_overtime',
  TOO_EARLY_LEFT_EARLY = 'too_early_left_early',
  LATE_OVERTIME = 'late_overtime',
  LATE_LEFT_EARLY = 'late_left_early'
}

// Employee-specific status type
export type EmployeeShiftStatus = 'To Begin' | 'In Progress' | 'Completed' | 'Completed Late' | 'Missed';

// Status priority for sorting and display (lower = higher priority/more attention needed)
export const STATUS_PRIORITY: Record<ShiftStatus, number> = {
  [ShiftStatus.MISSED]: 0,
  [ShiftStatus.NO_DEPARTURE_SCAN]: 1,
  [ShiftStatus.LATE]: 2,
  [ShiftStatus.LATE_OVERTIME]: 2,              // Both late AND overtime - high priority
  [ShiftStatus.LEFT_EARLY]: 3,
  [ShiftStatus.LATE_LEFT_EARLY]: 3,            // Late AND left early
  [ShiftStatus.OVERTIME]: 4,
  [ShiftStatus.TOO_EARLY_OVERTIME]: 4,         // Early arrival but overtime
  [ShiftStatus.IN_PROGRESS]: 5,
  [ShiftStatus.TOO_EARLY_LEFT_EARLY]: 5,       // Early arrival AND left early
  [ShiftStatus.TOO_EARLY]: 6,
  [ShiftStatus.PENDING]: 7,
  [ShiftStatus.COMPLETED_LATE]: 8,
  [ShiftStatus.ON_TIME]: 9,
  [ShiftStatus.COMPLETED]: 10,
  [ShiftStatus.UNASSIGNED]: 11,
  [ShiftStatus.PLANNING]: 12,
};

// Status metadata configuration
export interface ShiftStatusConfig {
  label: string;
  description: string;
  color: {
    background: string;
    text: string;
    border: string;
  };
  badgeColor: {
    background: string;
    text: string;
    border: string;
  };
  icon: LucideIcon;
  priority: number;
  isActive: boolean;
  requiresAttention: boolean;
}

// Comprehensive status configurations
export const SHIFT_STATUS_CONFIG: Record<ShiftStatus, ShiftStatusConfig> = {
  [ShiftStatus.PENDING]: {
    label: 'Pending',
    description: 'Shift is scheduled and awaiting start',
    color: {
      background: 'bg-blue-500/60 dark:bg-blue-400/60',
      text: 'text-white',
      border: 'border-blue-300 dark:border-blue-600'
    },
    badgeColor: {
      background: 'bg-blue-500 dark:bg-blue-400',
      text: 'text-white',
      border: 'border-blue-300 dark:border-blue-600'
    },
    icon: Clock,
    priority: STATUS_PRIORITY[ShiftStatus.PENDING],
    isActive: false,
    requiresAttention: false
  },
  [ShiftStatus.IN_PROGRESS]: {
    label: 'In Progress',
    description: 'Shift is currently active',
    color: {
      background: 'bg-emerald-500/60 dark:bg-emerald-400/60',
      text: 'text-white',
      border: 'border-emerald-300 dark:border-emerald-600'
    },
    badgeColor: {
      background: 'bg-emerald-500 dark:bg-emerald-400',
      text: 'text-white',
      border: 'border-emerald-300 dark:border-emerald-600'
    },
    icon: Play,
    priority: STATUS_PRIORITY[ShiftStatus.IN_PROGRESS],
    isActive: true,
    requiresAttention: false
  },
  [ShiftStatus.COMPLETED]: {
    label: 'Completed',
    description: 'Shift was completed successfully',
    color: {
      background: 'bg-green-500/60 dark:bg-green-400/60',
      text: 'text-white',
      border: 'border-green-300 dark:border-green-600'
    },
    badgeColor: {
      background: 'bg-green-500 dark:bg-green-400',
      text: 'text-white',
      border: 'border-green-300 dark:border-green-600'
    },
    icon: CheckCircle,
    priority: STATUS_PRIORITY[ShiftStatus.COMPLETED],
    isActive: false,
    requiresAttention: false
  },
  [ShiftStatus.COMPLETED_LATE]: {
    label: 'Completed Late',
    description: 'Shift was completed but employee arrived late',
    color: {
      background: 'bg-yellow-500/60 dark:bg-yellow-400/60',
      text: 'text-white',
      border: 'border-yellow-300 dark:border-yellow-600'
    },
    badgeColor: {
      background: 'bg-yellow-500 dark:bg-yellow-400',
      text: 'text-white',
      border: 'border-yellow-300 dark:border-yellow-600'
    },
    icon: AlertCircle,
    priority: STATUS_PRIORITY[ShiftStatus.COMPLETED_LATE],
    isActive: false,
    requiresAttention: true
  },
  [ShiftStatus.MISSED]: {
    label: 'Missed',
    description: 'Employee did not arrive for their shift',
    color: {
      background: 'bg-red-500/60 dark:bg-red-400/60',
      text: 'text-white',
      border: 'border-red-300 dark:border-red-600'
    },
    badgeColor: {
      background: 'bg-red-500 dark:bg-red-400',
      text: 'text-white',
      border: 'border-red-300 dark:border-red-600'
    },
    icon: XCircle,
    priority: STATUS_PRIORITY[ShiftStatus.MISSED],
    isActive: false,
    requiresAttention: false
  },
  [ShiftStatus.UNASSIGNED]: {
    label: 'Unassigned',
    description: 'Shift is scheduled but no employee assigned',
    color: {
      background: 'bg-orange-500/60 dark:bg-orange-400/60',
      text: 'text-white',
      border: 'border-orange-300 dark:border-orange-600'
    },
    badgeColor: {
      background: 'bg-orange-500 dark:bg-orange-400',
      text: 'text-white',
      border: 'border-orange-300 dark:border-orange-600'
    },
    icon: UserX,
    priority: STATUS_PRIORITY[ShiftStatus.UNASSIGNED],
    isActive: false,
    requiresAttention: true
  },
  [ShiftStatus.ON_TIME]: {
    label: 'On Time',
    description: 'Employee arrived on time for their shift',
    color: {
      background: 'bg-green-500/60 dark:bg-green-400/60',
      text: 'text-white',
      border: 'border-green-300 dark:border-green-600'
    },
    badgeColor: {
      background: 'bg-green-500 dark:bg-green-400',
      text: 'text-white',
      border: 'border-green-300 dark:border-green-600'
    },
    icon: CheckCircle,
    priority: STATUS_PRIORITY[ShiftStatus.ON_TIME],
    isActive: false,
    requiresAttention: false
  },
  [ShiftStatus.LATE]: {
    label: 'Late Arrival',
    description: 'Employee arrived late for their shift',
    color: {
      background: 'bg-amber-500/60 dark:bg-amber-400/60',
      text: 'text-white',
      border: 'border-amber-300 dark:border-amber-600'
    },
    badgeColor: {
      background: 'bg-amber-500 dark:bg-amber-400',
      text: 'text-white',
      border: 'border-amber-300 dark:border-amber-600'
    },
    icon: AlertCircle,
    priority: STATUS_PRIORITY[ShiftStatus.LATE],
    isActive: true,
    requiresAttention: true
  },
  [ShiftStatus.TOO_EARLY]: {
    label: 'Too Early',
    description: 'Employee arrived 30+ minutes before scheduled start time',
    color: {
      background: 'bg-primary/60',
      text: 'text-white',
      border: 'border-primary/30'
    },
    badgeColor: {
      background: 'bg-primary',
      text: 'text-white',
      border: 'border-primary/30'
    },
    icon: Clock,
    priority: STATUS_PRIORITY[ShiftStatus.TOO_EARLY],
    isActive: true,
    requiresAttention: true
  },
  [ShiftStatus.LEFT_EARLY]: {
    label: 'Left Early',
    description: 'Employee left before their scheduled end time',
    color: {
      background: 'bg-orange-500/60 dark:bg-orange-400/60',
      text: 'text-white',
      border: 'border-orange-300 dark:border-orange-600'
    },
    badgeColor: {
      background: 'bg-orange-500 dark:bg-orange-400',
      text: 'text-white',
      border: 'border-orange-300 dark:border-orange-600'
    },
    icon: ArrowRightFromLine,
    priority: STATUS_PRIORITY[ShiftStatus.LEFT_EARLY],
    isActive: false,
    requiresAttention: true
  },
  [ShiftStatus.OVERTIME]: {
    label: 'Overtime',
    description: 'Employee is working beyond their scheduled shift time',
    color: {
      background: 'bg-indigo-500/60 dark:bg-indigo-400/60',
      text: 'text-white',
      border: 'border-indigo-300 dark:border-indigo-600'
    },
    badgeColor: {
      background: 'bg-indigo-500 dark:bg-indigo-400',
      text: 'text-white',
      border: 'border-indigo-300 dark:border-indigo-600'
    },
    icon: Clock,
    priority: STATUS_PRIORITY[ShiftStatus.OVERTIME],
    isActive: true,
    requiresAttention: true
  },
  [ShiftStatus.NO_DEPARTURE_SCAN]: {
    label: 'No Departure',
    description: 'Employee did not scan out after working overtime',
    color: {
      background: 'bg-red-600/60 dark:bg-red-500/60',
      text: 'text-white',
      border: 'border-red-400 dark:border-red-700'
    },
    badgeColor: {
      background: 'bg-red-600 dark:bg-red-500',
      text: 'text-white',
      border: 'border-red-400 dark:border-red-700'
    },
    icon: XCircle,
    priority: STATUS_PRIORITY[ShiftStatus.NO_DEPARTURE_SCAN],
    isActive: false,
    requiresAttention: true
  },
  [ShiftStatus.PLANNING]: {
    label: 'Planning',
    description: 'Shift is being planned from a day template',
    color: {
      background: 'bg-gray-500/40 dark:bg-gray-400/40',
      text: 'text-white',
      border: 'border-dashed border-gray-400 dark:border-gray-500'
    },
    badgeColor: {
      background: 'bg-gray-500 dark:bg-gray-400',
      text: 'text-white',
      border: 'border-gray-300 dark:border-gray-600'
    },
    icon: Calendar,
    priority: STATUS_PRIORITY[ShiftStatus.PLANNING],
    isActive: false,
    requiresAttention: false
  },
  // Composite statuses (arrival + departure combinations)
  [ShiftStatus.TOO_EARLY_OVERTIME]: {
    label: 'Early + Overtime',
    description: 'Employee arrived 30+ minutes early and worked past scheduled end',
    color: {
      background: 'bg-gradient-to-r from-primary/60 to-indigo-500/60',
      text: 'text-white',
      border: 'border-primary/30'
    },
    badgeColor: {
      background: 'bg-gradient-to-r from-primary to-indigo-500',
      text: 'text-white',
      border: 'border-primary/30'
    },
    icon: Clock,
    priority: STATUS_PRIORITY[ShiftStatus.TOO_EARLY_OVERTIME],
    isActive: true,
    requiresAttention: true
  },
  [ShiftStatus.TOO_EARLY_LEFT_EARLY]: {
    label: 'Early + Left Early',
    description: 'Employee arrived 30+ minutes early but left before scheduled end',
    color: {
      background: 'bg-gradient-to-r from-primary/60 to-orange-500/60',
      text: 'text-white',
      border: 'border-primary/30'
    },
    badgeColor: {
      background: 'bg-gradient-to-r from-primary to-orange-500',
      text: 'text-white',
      border: 'border-primary/30'
    },
    icon: ArrowRightFromLine,
    priority: STATUS_PRIORITY[ShiftStatus.TOO_EARLY_LEFT_EARLY],
    isActive: false,
    requiresAttention: true
  },
  [ShiftStatus.LATE_OVERTIME]: {
    label: 'Late + Overtime',
    description: 'Employee arrived late but worked past scheduled end',
    color: {
      background: 'bg-gradient-to-r from-amber-500/60 to-indigo-500/60',
      text: 'text-white',
      border: 'border-amber-300 dark:border-amber-600'
    },
    badgeColor: {
      background: 'bg-gradient-to-r from-amber-500 to-indigo-500',
      text: 'text-white',
      border: 'border-amber-300 dark:border-amber-600'
    },
    icon: Clock,
    priority: STATUS_PRIORITY[ShiftStatus.LATE_OVERTIME],
    isActive: true,
    requiresAttention: true
  },
  [ShiftStatus.LATE_LEFT_EARLY]: {
    label: 'Late + Left Early',
    description: 'Employee arrived late and left before scheduled end',
    color: {
      background: 'bg-gradient-to-r from-amber-500/60 to-orange-500/60',
      text: 'text-white',
      border: 'border-amber-300 dark:border-amber-600'
    },
    badgeColor: {
      background: 'bg-gradient-to-r from-amber-500 to-orange-500',
      text: 'text-white',
      border: 'border-amber-300 dark:border-amber-600'
    },
    icon: ArrowRightFromLine,
    priority: STATUS_PRIORITY[ShiftStatus.LATE_LEFT_EARLY],
    isActive: false,
    requiresAttention: true
  }
};

// Business rules configuration
export interface ShiftStatusBusinessRules {
  lateArrivalThresholdMinutes: number;
  gracePeriodMinutes: number;
  autoMissedAfterMinutes: number;
  earlyArrivalThresholdMinutes: number;
  earlyDepartureThresholdMinutes: number;
  enableOvertimeTracking: boolean;
  overtimeThresholdMinutes: number;
  overtimeTimeoutHours: number;
  requireArrivalConfirmation: boolean;
}

export const DEFAULT_BUSINESS_RULES: ShiftStatusBusinessRules = {
  lateArrivalThresholdMinutes: 5,
  gracePeriodMinutes: 15,
  autoMissedAfterMinutes: 0,
  earlyArrivalThresholdMinutes: 30,
  earlyDepartureThresholdMinutes: 15,
  enableOvertimeTracking: true,
  overtimeThresholdMinutes: 15,
  overtimeTimeoutHours: 8,
  requireArrivalConfirmation: true
};

// Query result interfaces with enhanced typing
export interface ShiftWithEmployee extends Shift {
  employees: Pick<Employee, 'name' | 'email'> | null;
  calculated_status?: ShiftStatus;
  arrival_time?: string | null;
  departure_time?: string | null;
}

export interface ShiftQueryResult {
  id: string;
  start_time: string;
  end_time: string;
  title: string | null;
  employee_id: string | null;
  employees: { name: string } | null;
  calculated_status?: ShiftStatus;
}

export interface TimeLogQueryResult {
  id: string;
  business_id: string;
  employee_id: string;
  event_type: 'arrival' | 'departure';
  scanned_at: string;
  created_at: string;
}

// Enhanced employee shift interface
export interface EmployeeShiftWithStatus {
  id: string;
  shifts: {
    start_time: string;
    end_time: string;
  } | null;
  actual_arrival_time?: string | null;
  actual_departure_time?: string | null;
  calculated_status: ShiftStatus;
}


// Utility type guards
export const isValidShiftStatus = (status: string): status is ShiftStatus => {
  return Object.values(ShiftStatus).includes(status as ShiftStatus);
};

export const requiresAttention = (status: ShiftStatus): boolean => {
  return SHIFT_STATUS_CONFIG[status].requiresAttention;
};

export const isActiveStatus = (status: ShiftStatus): boolean => {
  return SHIFT_STATUS_CONFIG[status].isActive;
};

// Export all status values for convenience
export const ALL_SHIFT_STATUSES = Object.values(ShiftStatus);
export const ATTENTION_REQUIRED_STATUSES = ALL_SHIFT_STATUSES.filter(requiresAttention);
export const ACTIVE_STATUSES = ALL_SHIFT_STATUSES.filter(isActiveStatus);